import { httpClient, RequestOptions } from './httpClient';
import { LoginCredentials, AuthResponse, TwoFactorChallenge, LoginResult } from '../types/auth';
import { getSubdomain } from '../utils/tenantUtils';
import { formatProfilePictureUrl } from '../utils/imageUtils';
import { clearUserDataForLogin, clearUserDataForLogout } from '../utils/localStorageCleanup';

class AuthService {
  async login(credentials: LoginCredentials & { subdomain?: string }): Promise<LoginResult> {
    // Clear any previous user's data before logging in new user
    // This prevents previous user's preferences from interfering with the new session
    // Note: This preserves tenantId to maintain the current tenant context
    clearUserDataForLogin();

    // Check if we have a subdomain in the credentials or from the URL
    const currentSubdomain = credentials.subdomain || this.getCurrentSubdomain();

    // Create a copy of the credentials to avoid modifying the original
    const loginPayload = { ...credentials };

    const options: RequestOptions = {};

    // If we have a subdomain from either source, ensure it's included in the request
    if (currentSubdomain) {
      loginPayload.subdomain = currentSubdomain;

      if (loginPayload.tenantId) {
        // manually append a tenant id from the login parameters.
        options.headers = {
          'X-Tenant-ID': loginPayload.tenantId
        };
      }

      // Remove any tenantId property to avoid confusion
      delete loginPayload.tenantId;
    }

    // Make the API request with the enhanced payload
    const response = await httpClient.post<any>('api/auth/login', loginPayload);

    // 2FA: the server requires an email OTP before issuing a session. Don't store
    // a token yet — just hand the challenge back to the caller.
    if (response && response.twoFactorRequired) {
      if (currentSubdomain) {
        localStorage.setItem('tenantId', currentSubdomain);
      }
      return response as TwoFactorChallenge;
    }

    if (!response.user || !response.accessToken) {
      throw new Error('Invalid response from server');
    }

    // Always store auth data in localStorage
    localStorage.setItem('token', response.accessToken);
    localStorage.setItem('user', JSON.stringify(response.user));

    // Store the tenant ID in localStorage if we have it from any source
    if (currentSubdomain) {
      localStorage.setItem('tenantId', currentSubdomain);
    }

    return response as AuthResponse;
  }

  /** Complete login by submitting the emailed OTP code (with the pre-auth token). */
  async verifyOtp(params: {
    challengeId: string;
    code: string;
    rememberDevice: boolean;
    preAuthToken: string;
  }): Promise<AuthResponse> {
    const response = await httpClient.post<AuthResponse>(
      'api/auth/verify-otp',
      {
        challengeId: params.challengeId,
        code: params.code,
        rememberDevice: params.rememberDevice
      },
      { headers: { Authorization: `Bearer ${params.preAuthToken}` } }
    );

    if (!response.user || !response.accessToken) {
      throw new Error('Invalid response from server');
    }

    localStorage.setItem('token', response.accessToken);
    localStorage.setItem('user', JSON.stringify(response.user));
    return response;
  }

  /** Request a fresh OTP for an in-progress challenge. */
  async resendOtp(params: { challengeId: string; preAuthToken: string }): Promise<void> {
    await httpClient.post(
      'api/auth/resend-otp',
      { challengeId: params.challengeId },
      { headers: { Authorization: `Bearer ${params.preAuthToken}` } }
    );
  }

  async getTrustedDevices(): Promise<{ devices: any[] }> {
    return await httpClient.get('api/users/trusted-devices');
  }

  async revokeTrustedDevice(id: string): Promise<void> {
    await httpClient.delete(`api/users/trusted-devices/${id}`);
  }

  async revokeAllTrustedDevices(): Promise<void> {
    await httpClient.post('api/users/trusted-devices/revoke-all');
  }

  async logout(): Promise<void> {
    try {
      await httpClient.post('api/auth/logout');
    } finally {
      // Use comprehensive cleanup to clear all user-specific data including tenantId
      clearUserDataForLogout();

      // Also clear any remaining auth-specific items that might not be covered
      localStorage.removeItem('pendingLogin');
      sessionStorage.removeItem('pendingLogin');
    }
  }

  getAuthToken(): string | null {
    // Check sessionStorage first, then localStorage
    return sessionStorage.getItem('token') || localStorage.getItem('token');
  }

  getUser(): any {
    const sessionUser = sessionStorage.getItem('user');
    const localUser = localStorage.getItem('user');
    const userStr = sessionUser || localUser;

    if (!userStr) return null;

    const user = JSON.parse(userStr);

    // Ensure profile picture URL is properly formatted if it exists
    if (user && user.pfp_url) {
      // Create a new user object with the formatted profile picture URL
      return {
        ...user,
        // Store the original URL in a separate property for debugging
        original_pfp_url: user.pfp_url,
        // Format the URL for proper display
        pfp_url: formatProfilePictureUrl(user.pfp_url)
      };
    }

    return user;
  }

  getCurrentSubdomain(): string | null {
    return getSubdomain();
  }

  async captcha(token: string): Promise<any> {
    return await httpClient.post('api/auth/captcha', { token });
  }
}

export const authService = new AuthService();
